/**
 * DifferentialsCell - the temperature differential pairs of one location,
 * shown as small "vs <name>" chips, for the pour create wizard's table and
 * the pour edit page's table (and their mobile cards). At most one chip
 * shows, a long name cut short, then "+N"; the cell's tooltip lists them all,
 * so many pairs or long names keep the row one line high.
 *
 * When editable, clicking the cell (or the small edit button) opens a
 * checklist of the eligible partners. Ticks apply when the list closes
 * (clicking away or Escape), the same click-a-cell pattern as the date
 * cells on these tables, so there are no OK / Cancel buttons. onChange runs
 * once on close, and only when the selection changed.
 *
 * The 'button' variant is a small "Differentials (2)" button with the
 * partners in its tooltip, for the channel rows of the add dialog.
 *
 * Presentational only: the caller works out the partners (both sides of
 * each pair) and which locations are eligible.
 */
import React, { useRef, useState } from 'react';
import { Box, Button, Checkbox, Chip, FormControlLabel, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';

export interface DiffPartnerOption {
  id: string;
  label: string;
}

export interface DifferentialsCellProps {
  /** Current partners, shown as chips "vs <label>". */
  partners: DiffPartnerOption[];
  /** Eligible partners for the checklist. Current partners missing from
   *  this list are still listed so they can be removed. */
  options: DiffPartnerOption[];
  editable: boolean;
  /** The full new list of partner ids, called once when the checklist
   *  closes and only if the selection changed. */
  onChange: (nextIds: string[]) => void;
  /** Shows the chips but blocks editing, e.g. while saving. */
  disabled?: boolean;
  /** Shown when there are no partners. Defaults to "None"; pass "-" for
   *  rows that can't have differentials. */
  emptyText?: string;
  /** 'cell' (default) makes the whole cell clickable, for desktop tables.
   *  'inline' is a compact chips + button line for mobile cards. 'button'
   *  is one small button, for a channel row of the add dialog. */
  variant?: 'cell' | 'inline' | 'button';
  /** Accessible name of the 'button' variant, e.g. "Differentials for Ch 1". */
  buttonLabel?: string;
}

const CHIP_SX = { height: 20, maxWidth: 100, fontSize: '0.7rem' };

/** Chips shown before the rest fold into "+N". */
const MAX_CHIPS = 1;

const sameIds = (a: ReadonlyArray<string>, b: ReadonlyArray<string>): boolean => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
};

const DifferentialsCell: React.FC<DifferentialsCellProps> = ({
  partners,
  options,
  editable,
  onChange,
  disabled = false,
  emptyText = 'None',
  variant = 'cell',
  buttonLabel = 'Differentials',
}) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  // The open checklist's anchor (the cell); null while closed.
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<string[]>([]);
  const open = anchorEl !== null;

  const canEdit = editable && !disabled;
  const partnerIds = partners.map((p) => p.id);

  // Eligible partners first, then any current partner that is no longer
  // eligible so it can still be unticked.
  const optionIds = new Set(options.map((o) => o.id));
  const items = [...options, ...partners.filter((p) => !optionIds.has(p.id))];

  // An editable cell handles its own click, so a clickable parent row or
  // card does not also react to it.
  const openList = (e: React.MouseEvent) => {
    if (!canEdit) return;
    e.stopPropagation();
    if (open) return;
    setDraft(partnerIds);
    setAnchorEl(anchorRef.current);
  };

  const closeList = () => {
    if (!open) return;
    setAnchorEl(null);
    if (sameIds(draft, partnerIds)) return;
    const picked = new Set(draft);
    onChange(items.filter((item) => picked.has(item.id)).map((item) => item.id));
  };

  const toggle = (id: string, checked: boolean) => {
    setDraft((prev) => (checked ? (prev.includes(id) ? prev : [...prev, id]) : prev.filter((x) => x !== id)));
  };

  const hidden = partners.length - MAX_CHIPS;
  const chips =
    partners.length > 0 ? (
      <>
        {partners.slice(0, MAX_CHIPS).map((p) => (
          <Chip key={p.id} label={`vs ${p.label}`} size="small" variant="outlined" sx={CHIP_SX} />
        ))}
        {hidden > 0 && (
          <Chip label={`+${hidden}`} size="small" sx={{ ...CHIP_SX, fontWeight: 600 }} />
        )}
      </>
    ) : (
      <Typography variant="body2" color="text.secondary" component="span" sx={{ fontSize: '0.8rem' }}>
        {emptyText}
      </Typography>
    );

  const editButton = editable && (
    <IconButton
      size="small"
      aria-label="Edit differentials"
      disabled={disabled}
      onClick={openList}
      sx={{ p: 0.25 }}
    >
      {partners.length > 0 ? <EditIcon sx={{ fontSize: 16 }} /> : <AddIcon sx={{ fontSize: 16 }} />}
    </IconButton>
  );

  const isCell = variant === 'cell';

  const trigger =
    variant === 'button' ? (
      <Box ref={anchorRef} data-testid="differentials-cell" sx={{ display: 'flex', width: '100%' }}>
        <Tooltip
          title={partners.length > 0 ? partners.map((p) => `vs ${p.label}`).join(', ') : ''}
          disableHoverListener={open}
        >
          <Box component="span" sx={{ display: 'flex', width: '100%' }}>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              fullWidth
              aria-label={buttonLabel}
              disabled={!canEdit}
              onClick={openList}
              sx={{
                minWidth: 0,
                px: 1,
                py: 0.25,
                mt: 0.75,
                fontSize: '0.75rem',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                borderColor: 'grey.400',
              }}
            >
              {partners.length > 0 ? `Differentials (${partners.length})` : 'Differentials'}
            </Button>
          </Box>
        </Tooltip>
      </Box>
    ) : (
      <Tooltip
        title={partners.length > 0 ? partners.map((p) => `vs ${p.label}`).join(', ') : ''}
        disableHoverListener={open}
        placement="top-start"
      >
        <Box
          ref={anchorRef}
          data-testid="differentials-cell"
          onClick={isCell ? openList : undefined}
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 0,
            ...(isCell && canEdit
              ? { cursor: 'pointer', '&:hover': { color: 'primary.main' } }
              : {}),
          }}
        >
          {chips}
          {editButton}
        </Box>
      </Tooltip>
    );

  return (
    <>
      {trigger}

      {/* A sibling of the cell, and clicks stop here, so clicking away (the
          backdrop) can't bubble up through React and reopen the list. */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closeList}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { p: 1.5, minWidth: 200, maxWidth: 'min(320px, calc(100vw - 32px))' } },
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
          Temperature differential with
        </Typography>
        {items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
            Add another location to set a differential.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', maxHeight: 320, overflowY: 'auto', mt: 0.25 }}>
            {items.map((item) => (
              <FormControlLabel
                key={item.id}
                sx={{ ml: 0, mr: 0, minWidth: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={draft.includes(item.id)}
                    onChange={(e) => toggle(item.id, e.target.checked)}
                    inputProps={{ 'aria-label': `Pair with ${item.label}` }}
                    sx={{ p: 0.5 }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.85rem', overflowWrap: 'anywhere' }}>
                    {item.label}
                  </Typography>
                }
              />
            ))}
          </Box>
        )}
      </Popover>
    </>
  );
};

export default DifferentialsCell;
